export const MAX_IMAGES = 50;

export const IMAGE_ACCEPT =
  "image/jpeg,image/png,image/webp,image/avif,image/gif,image/bmp,image/tiff,.jpg,.jpeg,.png,.webp,.avif,.gif,.bmp,.tif,.tiff";

export type OptimizedImage = {
  id: string;
  originalFile: File;
  originalSize: number;
  name: string;
  blob: Blob | null;
  outputSize: number;
  previewUrl: string | null;
  status: "pending" | "processing" | "done" | "error";
  error?: string;
};

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function savingsPercent(original: number, optimized: number): number {
  if (original <= 0) return 0;
  return Math.round((1 - optimized / original) * 100);
}

export function stripExtension(filename: string): string {
  return filename.replace(/\.[^.]+$/, "") || filename;
}

export function isImageFile(file: File): boolean {
  if (file.type.startsWith("image/")) return true;
  return /\.(jpe?g|png|webp|avif|gif|bmp|tiff?)$/i.test(file.name);
}

export async function convertImageToWebp(
  file: File,
  scalePercent: number,
  quality = 0.82,
  options?: { stripExif?: boolean },
): Promise<Blob> {
  const stripExif = options?.stripExif ?? true;
  const scale = Math.max(0.1, Math.min(1, scalePercent / 100));

  if (!stripExif && scale >= 1) {
    return file;
  }

  const bitmap = await createImageBitmap(file);
  try {
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");

    ctx.drawImage(bitmap, 0, 0, w, h);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob ? resolve(blob) : reject(new Error("WebP export failed")),
        "image/webp",
        quality,
      );
    });
  } finally {
    bitmap.close();
  }
}

export function outputExtension(blob: Blob): string {
  switch (blob.type) {
    case "image/jpeg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/gif":
      return "gif";
    case "image/webp":
      return "webp";
    case "image/avif":
      return "avif";
    case "image/bmp":
      return "bmp";
    case "image/tiff":
      return "tiff";
    default:
      return "webp";
  }
}

export function outputFilename(name: string, blob: Blob): string {
  const safeName = name.replace(/[^\w.-]+/g, "_") || "image";
  return `${safeName}.${outputExtension(blob)}`;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function downloadImagesAsZip(
  items: Pick<OptimizedImage, "name" | "blob">[],
  zipName = "optimized-images.zip",
) {
  const JSZip = (await import("jszip")).default;
  const zip = new JSZip();
  for (const item of items) {
    if (!item.blob) continue;
    zip.file(outputFilename(item.name, item.blob), item.blob);
  }
  const content = await zip.generateAsync({ type: "blob" });
  downloadBlob(content, zipName);
}
